import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Modal } from '../common'
import { QuickAddClientForm } from './QuickAddClientForm'
import { seatService } from '../../services'
import type { Client } from '../../types/database.types'

interface QuickAddClientModalProps {
  isOpen: boolean
  onClose: () => void
  onClientCreated: (client: Client) => void
  providerId: string
  providerName: string
  initialSearchQuery?: string
}

export function QuickAddClientModal({
  isOpen,
  onClose,
  onClientCreated,
  providerId,
  providerName,
  initialSearchQuery = '',
}: QuickAddClientModalProps) {
  const [canAddClient, setCanAddClient] = useState<boolean | null>(null)
  const [isCheckingLimit, setIsCheckingLimit] = useState(false)

  // Check client limit when modal opens
  useEffect(() => {
    if (isOpen && providerId) {
      setIsCheckingLimit(true)
      seatService
        .canAddClient(providerId)
        .then(setCanAddClient)
        .finally(() => setIsCheckingLimit(false))
    }
  }, [isOpen, providerId])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCanAddClient(null)
    }
  }, [isOpen])

  // Smart pre-fill: detect if search query looks like an email
  const isEmail = initialSearchQuery.includes('@') && initialSearchQuery.includes('.')
  const initialName = isEmail ? '' : initialSearchQuery
  const initialEmail = isEmail ? initialSearchQuery : ''

  const handleSuccess = (client: Client) => {
    onClientCreated(client)
    onClose()
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add New Client"
      size="sm"
    >
      {isCheckingLimit ? (
        <div className="py-8 text-center">
          <div className="inline-flex items-center gap-2 text-gray-500">
            <svg
              className="animate-spin h-5 w-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Checking availability...
          </div>
        </div>
      ) : canAddClient === false ? (
        <div className="py-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-yellow-100 flex items-center justify-center">
            <svg
              className="w-6 h-6 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Client Limit Reached
          </h3>
          <p className="text-gray-600 mb-6">
            You've reached the maximum number of clients for your current plan.
            Upgrade to add more clients.
          </p>
          <div className="flex justify-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <Link
              to="/dashboard/subscription"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              onClick={onClose}
            >
              Upgrade Plan
            </Link>
          </div>
        </div>
      ) : (
        <QuickAddClientForm
          providerId={providerId}
          providerName={providerName}
          onSuccess={handleSuccess}
          onCancel={onClose}
          initialName={initialName}
          initialEmail={initialEmail}
        />
      )}
    </Modal>
  )
}
